import React, { useMemo } from "react";
import clsx from "clsx";

import {
  getStudentAnswersByQuestion,
  getLessonFrom,
  getIssueFrom,
  getQuestionFrom,
} from "../../../../libs/ActiveLearning";
import {
  IActiveLearningMaterialLessonsData,
  IActiveLearningStudentAnswers,
  IActiveLearningAllAnswers,
} from "../../../../state";
import TreeCard from "../../../../atoms/ActiveLearning/TreeCard";
import styles from "../LessonsAnswerData.module.scss";

type Lesson14Pros = {
  materialCode: string;
  lessonNo: number;
  activeLearningMaterialLessons: IActiveLearningMaterialLessonsData[];
  activeLearningAnswers:
    | IActiveLearningStudentAnswers
    | IActiveLearningAllAnswers
    | null;
};

export const Lesson14: React.FC<Lesson14Pros> = ({
  materialCode,
  lessonNo,
  activeLearningMaterialLessons,
  activeLearningAnswers,
}) => {
  const activeLearningMaterialLessonsByCode = useMemo(() => {
    return activeLearningMaterialLessons.filter(
      data => data.code === materialCode,
    );
  }, [activeLearningMaterialLessons, materialCode]);

  const titleL4 = useMemo(() => {
    return getLessonFrom(activeLearningMaterialLessonsByCode, lessonNo)?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL4I1 = useMemo(() => {
    return getIssueFrom(activeLearningMaterialLessonsByCode, lessonNo, 1)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL4I1Q1 = useMemo(() => {
    return getQuestionFrom(activeLearningMaterialLessonsByCode, lessonNo, 1, 1)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL4I2 = useMemo(() => {
    return getIssueFrom(activeLearningMaterialLessonsByCode, lessonNo, 2)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const answersL4I1Q1 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      1,
      1,
    );

    return answers.length > 0
      ? answers.map(answer => answer.contents.value).join("")
      : "回答データがありません。";
  }, [materialCode, activeLearningAnswers, lessonNo]);

  const answersL4I1Q2 = useMemo(() => {
    return getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      1,
      2,
    );
  }, [materialCode, activeLearningAnswers, lessonNo]);

  const answersL3I2Q1 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      3,
      2,
      1,
    );

    return answers.length > 0
      ? answers.map(answer => answer.contents.value).join("")
      : "回答データがありません。";
  }, [materialCode, activeLearningAnswers]);

  const answersL4I2Q1 = useMemo(() => {
    return getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      2,
      1,
    );
  }, [materialCode, activeLearningAnswers, lessonNo]);

  return (
    <div className={styles.lesson}>
      <p className={styles.title}>
        Lesson{lessonNo < 10 ? `0${lessonNo}` : lessonNo} {titleL4}
      </p>
      <div className={styles.inner}>
        <div className={styles.work}>
          <p className={styles.no}>WORK01</p>
          <p className={styles.heading}>{titleL4I1}</p>
          <div className={styles.question}>
            <p className={clsx(styles.heading, styles.dot)}>{titleL4I1Q1}</p>
            <p className={clsx(styles.answer, styles.high)}>{answersL4I1Q1}</p>
            <div className={styles.feedbackTextArea}>
              <div className={styles.block}>
                <p className={styles.title}>想定される行動</p>
                {answersL4I1Q2.length > 0 ? (
                  answersL4I1Q2.map(answer => (
                    <p key={answer.id} className={styles.feedbackAnswer}>
                      {answer.contents.good}
                    </p>
                  ))
                ) : (
                  <p className={styles.feedbackAnswer}>
                    回答データがありません。
                  </p>
                )}
              </div>
              <div className={styles.block}>
                <p className={styles.title}>
                  もっとこうだったらいのに/こうしたい
                </p>
                {answersL4I1Q2.length > 0 ? (
                  answersL4I1Q2.map(answer => (
                    <p key={answer.id} className={styles.feedbackAnswer}>
                      {answer.contents.motto}
                    </p>
                  ))
                ) : (
                  <p className={styles.feedbackAnswer}>
                    回答データがありません。
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.work}>
          <p className={styles.no}>WORK02</p>
          <div className={styles.question}>
            <p className={styles.heading}>{titleL4I2}</p>
            <div className={styles.tree}>
              <TreeCard title={"問い"} text={answersL3I2Q1} />
              <ul className={styles.branchList}>
                {answersL4I2Q1.length > 0 ? (
                  answersL4I2Q1.map(answer => (
                    <li key={answer.id} className={styles.branch}>
                      <div className={styles.labelWrapper}>
                        <span className={styles.label}>問題</span>
                      </div>
                      <p className={styles.answer}>{answer.contents.value}</p>
                    </li>
                  ))
                ) : (
                  <li className={styles.branch}>
                    <div className={styles.labelWrapper}>
                      <span className={styles.label}>問題</span>
                    </div>
                    <p className={styles.answer}>回答データがありません。</p>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Lesson14;
